// web/src/components/enroll/seal.ts — the client-side SEAL step (ADR-003 D1 / R11).
//
// This is where "plaintext never leaves the browser" is made true. Given the
// admitted members' PUBLIC keys, the plaintext secret, and the quorum policy, it:
//   1. seals the secret under a fresh random data key (the Envelope),
//   2. Shamir-splits ONLY the data key into N shares, threshold K,
//   3. ECDH-encrypts each share to its recipient's PUBLIC key (one WireShard each),
// then assembles the POST /records body carrying ONLY the opaque envelope params
// and per-member ciphertext shards. The data key and raw shares are produced and
// scrubbed inside crypto/sealSecret; nothing secret-adjacent crosses this boundary.
import { sealSecret } from '../../crypto';
import type { PublicKeyId, PublicKeyJwk } from '../../crypto';
import type {
  SealMemberInput,
  SealRecordInput,
  Sensitivity,
} from '../../../../shared/types';

/** One admitted member, with the PUBLIC key the initiator will encrypt a shard to. */
export interface SealMember {
  pubkeyId: string;
  email: string;
  /** The PUBLIC JWK from the registry binding (parsed). */
  publicKey: PublicKeyJwk;
}

export interface SealPolicy {
  recordId: string;
  description: string;
  sensitivity: Sensitivity;
  ownerId: string;
  thresholdK: number;
  timeoutSeconds: number;
}

/**
 * Build the SEAL request body entirely in-browser. The returned `SealRecordInput`
 * contains ONLY public envelope params + opaque per-member ciphertext — it is
 * safe to POST to /records. Throws if the quorum is structurally invalid; the
 * crypto layer raises on a degenerate (k, n).
 */
export async function buildSealRequest(
  sessionId: string,
  plaintext: string,
  members: SealMember[],
  policy: SealPolicy,
): Promise<SealRecordInput> {
  const recipients = members.map((m) => ({
    keyId: m.pubkeyId as PublicKeyId,
    publicKey: m.publicKey,
  }));

  // sealSecret: envelope + one WireShard per recipient. Data key + raw shares are
  // generated and zeroed inside; only opaque artefacts are returned (R11).
  const { envelope, encryptedShares } = await sealSecret(
    new TextEncoder().encode(plaintext),
    recipients,
    policy.thresholdK,
  );

  // Pair each member with its WireShard by share index (1..N, the ceremony slot).
  const sealMembers: SealMemberInput[] = members.map((m, i) => {
    const shard = encryptedShares[i];
    return {
      pubkeyId: m.pubkeyId,
      email: m.email,
      shareIndex: shard.shareIndex,
      // The opaque WireShard is serialised as the store-and-forward ciphertext
      // (ADR-003 D8 baseline). The server cannot open it — it holds no private key.
      shardCiphertext: JSON.stringify(shard),
    };
  });

  return {
    sessionId,
    record: {
      id: policy.recordId,
      description: policy.description,
      sensitivity: policy.sensitivity,
      ownerId: policy.ownerId,
      thresholdK: policy.thresholdK,
      totalN: members.length,
      timeoutSeconds: policy.timeoutSeconds,
      // PUBLIC envelope params only — no key, no plaintext (no-material gate #4).
      envelopeMeta: {
        v: envelope.v,
        aeadAlg: 'AES-256-GCM',
        sssScheme: 'secrets.js-grempe/GF256',
        iv: envelope.iv,
      },
      // OPAQUE encrypted-secret blob (AES-GCM ciphertext under the Shamir-split
      // data key). The server can NEVER decrypt it — it holds no data key — so
      // storing/serving it preserves zero-trust (R11). Without it, a requester who
      // gathers K shares recovers the data key but has nothing to decrypt.
      envelopeCiphertext: envelope.ciphertext,
    },
    members: sealMembers,
  };
}

/** A client-generated record id (ULID-ish: time-sortable, base32, collision-safe enough). */
export function newRecordId(): string {
  const ts = Date.now().toString(36);
  const rand = Array.from(crypto.getRandomValues(new Uint8Array(10)))
    .map((b) => b.toString(36).padStart(2, '0'))
    .join('');
  return `rec_${ts}${rand}`;
}
